import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import {
  type AuthUser,
  type RegisterData,
  type AuthTokens,
  loginUser,
  registerUser,
  restoreSession,
  logoutUser,
  storeTokens,
  getStoredAccessToken,
  DEMO_USER,
} from "@/services/authService";
import { isApiConfigured, onTokensRefreshed } from "@/services/http";

// ── Context shape ─────────────────────────────────────────────────────────────

interface AuthContextValue {
  user:            AuthUser | null;
  accessToken:     string | null;
  isAuthenticated: boolean;
  isLoading:       boolean;
  isDemoMode:      boolean;
  login:           (email: string, password: string) => Promise<AuthUser>;
  register:        (data: RegisterData) => Promise<void>;
  logout:          () => void;
  setTokens:       (tokens: AuthTokens) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ── Provider ──────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user,        setUser]        = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(getStoredAccessToken());
  const [isLoading,   setIsLoading]   = useState(true);

  const isDemoMode = !isApiConfigured();

  // Keep the in-memory access token in sync whenever the HTTP layer refreshes
  // a short-lived access token automatically after a 401.
  useEffect(() => {
    onTokensRefreshed((accessToken) => setAccessToken(accessToken));
    return () => onTokensRefreshed(null);
  }, []);

  // On mount — restore session from stored tokens
  useEffect(() => {
    if (isDemoMode) {
      setIsLoading(false);
      return;
    }

    restoreSession()
      .then((session) => {
        if (session) {
          setUser(session.user);
          setAccessToken(session.accessToken);
        }
      })
      .finally(() => setIsLoading(false));
  }, [isDemoMode]);

  const setTokens = useCallback((tokens: AuthTokens) => {
    storeTokens(tokens.accessToken, tokens.refreshToken);
    setUser(tokens.user);
    setAccessToken(tokens.accessToken);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    if (isDemoMode) {
      // Demo mode — accept any credentials and show mock user
      setUser(DEMO_USER);
      setAccessToken("demo-token");
      return DEMO_USER;
    }
    const tokens = await loginUser(email, password);
    setTokens(tokens);
    return tokens.user;
  }, [isDemoMode, setTokens]);

  const register = useCallback(async (data: RegisterData) => {
    if (isDemoMode) {
      setUser({ ...DEMO_USER, name: data.name, email: data.email });
      setAccessToken("demo-token");
      return;
    }
    const tokens = await registerUser(data);
    setTokens(tokens);
  }, [isDemoMode, setTokens]);

  const logout = useCallback(() => {
    // Revoke the refresh token server-side, then clear local state immediately.
    void logoutUser();
    setUser(null);
    setAccessToken(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        isAuthenticated: !!user,
        isLoading,
        isDemoMode,
        login,
        register,
        logout,
        setTokens,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}